// Copyright (c) 2026 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useMemo } from 'react';
import { AddOutlined, Close } from '@mui/icons-material';
import { Box, Button, IconButton, TextField, Typography } from '@mui/material';
import { Select, SelectOptionProps } from '../../../../components/Form/Select';
import { createInstrumentKey, InstrumentKey, Instrument } from '../../../../utils/instrument';
import { isPositiveFloat } from '../../../../utils/isPositiveFloat';
import { Position } from '../../types';

const AssetOption = ({ option }: SelectOptionProps<Instrument>) => {
  return (
    <Typography variant="caption" sx={{ textTransform: 'uppercase' }} fontWeight="bold">
      {option.displayName}
    </Typography>
  );
};

type EligibleCollateralSelectProps = {
  position?: Position;
  onInstrumentChange: (instrumentKey: InstrumentKey) => void;
  onAmountChange: (amount: string) => void;
  selectedInstruments: Set<InstrumentKey>;
  eligibleInstruments: Instrument[];
  onRemove: () => void;
  showClose: boolean;
};

const EligibleCollateralSelect: React.FC<EligibleCollateralSelectProps> = ({
  position,
  onInstrumentChange,
  onAmountChange,
  selectedInstruments,
  eligibleInstruments,
  onRemove,
  showClose,
}) => {
  const selectLabel = useMemo(() => {
    if (!position || !position.id || !position.admin) {
      return 'Select collateral';
    }

    return position.id;
  }, [position]);

  const selectedKeys = new Set(position ? [createInstrumentKey(position.id, position.admin)] : []);

  const selectableInstruments = eligibleInstruments.filter((inst) => {
    const positionKey = position ? createInstrumentKey(position.id, position.admin) : null;
    return inst.key === positionKey || !selectedInstruments.has(inst.key);
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', mb: 1, gap: 1 }}>
      <Box sx={{ width: 1 }}>
        <Select
          label={selectLabel}
          options={selectableInstruments}
          getOptionKey={(inst) => inst.key}
          selectedKeys={selectedKeys}
          onOptionClick={(key) => onInstrumentChange(key as InstrumentKey)}
          Option={AssetOption}
          disabled={selectableInstruments.length === 0}
        />
      </Box>
      <TextField
        fullWidth
        value={position?.amount ?? '0'}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          if (isPositiveFloat(e.target.value)) {
            onAmountChange(e.target.value);
          }
        }}
        size="small"
        placeholder="amount"
        disabled={selectableInstruments.length === 0}
      />
      {showClose && (
        <Box>
          <IconButton size="small" onClick={onRemove}>
            <Close fontSize="small" color="action" />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

interface AssetSelectProps {
  eligibleInstruments: Instrument[];
  rows: { id: string; admin: string; amount: string; uuid: string }[];

  onAdd: () => void;
  onInstrumentChange: (uuid: string, instrumentKey: InstrumentKey) => void;
  onAmountChange: (uuid: string, amount: string) => void;
  onRemove: (uuid: string) => void;
}

export const AssetSelect: React.FC<AssetSelectProps> = ({
  eligibleInstruments,
  rows,
  onAdd,
  onInstrumentChange,
  onAmountChange,
  onRemove,
}) => {
  const selectedInstruments = new Set(
    rows.filter((r) => r.id && r.admin).map((r) => createInstrumentKey(r.id, r.admin)),
  );

  const showAddCollateralButton = rows.length < eligibleInstruments.length;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((a) => (
        <EligibleCollateralSelect
          position={a}
          onInstrumentChange={(key) => onInstrumentChange(a.uuid, key)}
          onAmountChange={(amount) => onAmountChange(a.uuid, amount)}
          onRemove={() => onRemove(a.uuid)}
          selectedInstruments={selectedInstruments}
          eligibleInstruments={eligibleInstruments}
          key={a.uuid}
          showClose={rows.length > 1}
        />
      ))}
      {showAddCollateralButton && (
        <Button
          sx={{ ml: 'auto' }}
          startIcon={<AddOutlined />}
          size="small"
          variant="text"
          onClick={onAdd}
        >
          Add Collateral
        </Button>
      )}
    </Box>
  );
};
